import FileSaver from 'file-saver';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import readXlsxFile from 'read-excel-file';
import { fileSizeConverter } from '../../../helpers';
import { getContractTypeByContractNumber } from '../../../helpers/component-helper-methods';
import smartHttp from '../../../helpers/smartHttp';
import AgGridComponent from '../../../sharedcomponents/ag-grid/AgGrid';
import ModalBox from '../../../sharedcomponents/modal-box/ModalBox';
import { ReactComponent as BulkUploadIcon } from './../../../assets/icons/common/bulk-upload.svg';
import { ReactComponent as CloudBigIcon } from './../../../assets/icons/common/cloud-big-icon.svg';
import { ReactComponent as CrossIcon } from './../../../assets/icons/common/cross-icon.svg';
import {
  Endpoints,
  ReduxAction,
  UserId,
  Verbiage,
  maxPageSize,
  BulkUploadUserEntitlementsTemplateType,
  Draft,
  PendingActive,
  GenericError,
} from '../../../constants';
import { ReactComponent as ExportIcon } from './../../../assets/icons/common/export.svg';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import '../bulk-upload-users/bulk-upload-users.scss';
import { Notify } from '../../../sharedcomponents/Alert/Notify';
import { persistDataStore } from './../../../redux/persistStore';

const BulkUploadUserEntitlements = ({
  showBulkUserEntitlements,
  handleCloseBulkUerEntitlements,
  contracts,
  getUpdatedUserEntitlements,
  isBPMReferral,
}) => {
  const gridRef = useRef(null);
  const locationContractsMapRef = useRef(new Map());

  const [loading, setLoading] = useState(false);
  const [isErrorGridShow, setIsErrorGridShow] = useState(false);
  const [isUploadBoxShow, setIsUploadBoxShow] = useState(true);
  const [uploadedUsers, setUploadedUsers] = useState([]);
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const [errorData, setErrorData] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sourceUsersGridStyle = { height: '35vh' };
  const vcnContract = contracts?.find(
    (cn) => cn?.mappedContractNumber || cn?.virtualContractNo,
  );
  const isVCN = !!vcnContract;

  const expandContractNumber = (cn) => {
    const normalized = String(cn || '').trim();
    if (!normalized) return [];
    const match = contracts?.find(
      (c) => c?.contractNumber === normalized || c?.id === normalized,
    );
    if (match?.isVirtualContract === 1 && match?.mappedContractNumber) {
      return match.mappedContractNumber
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [normalized];
  };

  useEffect(() => {
    const fetchLocationContracts = async () => {
      const contractNumbers = [
        ...new Set(
          contracts
            ?.filter((x) => x.checked)
            .flatMap((t) =>
              t.isVirtualContract && t.mappedContractNumber
                ? t.mappedContractNumber
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : t.id
                  ? [t.id]
                  : [],
            ) ?? [],
        ),
      ];
      if (contractNumbers.length === 0) return;
      try {
        const resp = await smartHttp.get(Endpoints.locationApi, {
          PageSize: maxPageSize,
          field: 'officeLocationId,allContractNumber',
          Filter: `contractNumber IN(${contractNumbers
            .map((c) => '"' + c + '"')
            .join(',')})`,
        });
        const map = new Map();
        (resp?.data?.results || []).forEach((loc) => {
          const set = map.get(loc.officeLocationId) || new Set();
          String(loc.allContractNumber || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((c) => set.add(c));
          map.set(loc.officeLocationId, set);
        });
        locationContractsMapRef.current = map;
      } catch (err) {}
    };
    fetchLocationContracts();
  }, [contracts]);

  const defaultConfig = {
    rowModelType: 'clientSide',
    isExportCSV: false,
    isExportExcel: false,
    pivotPanelShow: '',
    pagination: false,
    isAutoSizeColumns: true,
    enableCharts: false,
    rowGroupPanelShow: false,
    sideBar: false,
  };

  const defaultColdef = {
    resizable: true,
    enableRowGroup: false,
    enablePivot: false,
    enableValue: false,
    headerCheckboxSelection: false,
    // checkboxSelection: isFirstColumn,
  };

  const sourceUsersColumns = [
    {
      headerName: 'Email',
      field: 'email',
      tooltipField: 'email',
      resizable: true,
      initialFlex: 1,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'First Name',
      field: 'firstName',
      tooltipField: 'firstName',
      resizable: true,
      initialFlex: 1,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Last Name',
      field: 'lastName',
      tooltipField: 'lastName',
      initialFlex: 1,
      resizable: true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Office Location',
      field: 'officeLocation',
      tooltipField: 'officeLocation',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Job Function',
      field: 'jobFunction',
      tooltipField: 'jobFunction',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Role',
      field: 'designation',
      tooltipField: 'designation',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Entitlement',
      field: 'entitlement',
      tooltipField: 'entitlement',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'User ID',
      field: 'userId',
      tooltipField: 'userId',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Trading Entity Name',
      field: 'contact_Xref_Number',
      tooltipField: 'contact_Xref_Number',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
    {
      headerName: 'Errors',
      field: 'error',
      tooltipField: 'error',
      initialFlex: 1,
      resizable: true,
      // wrapText:true,
      cellClass: (params) => {
        return 'error-bulk-upload-row';
      },
    },
  ];

  const getRowError = (rowData) => {
    let rowError = '';
    if (rowData) {
      let fieldErrorDetails = [
        {
          field: 'first name',
          errorType: ['required', 'max', 'text'],
          maxLength: 80,
        },
        {
          field: 'last name',
          errorType: ['required', 'max', 'text'],
          maxLength: 80,
        },
        {
          field: 'email',
          errorType: ['required', 'max', 'email'],
          maxLength: 80,
        },
        {
          field: 'office location',
          errorType: ['required', 'dropDown'],
          dropDownIndex: 75,
        },
        {
          field: 'job function',
          errorType: ['required', 'dropDown'],
          dropDownIndex: 76,
        },
        {
          field: 'role',
          errorType: ['required', 'dropDown'],
          dropDownIndex: 77,
        },
        {
          field: 'entitlement',
          errorType: ['required', 'dropDown'],
          dropDownIndex: 78,
        },
      ];
      for (let row = 0; row < fieldErrorDetails.length; row++) {
        fieldErrorDetails[row].errorType.forEach((x) => {
          switch (x) {
            case 'required':
              if (!rowData[row])
                rowError =
                  rowError + `Please enter ${fieldErrorDetails[row].field}. `;
              break;
            case 'max':
              if (
                rowData[row] &&
                rowData[row].length > fieldErrorDetails[row].maxLength
              )
                rowError =
                  rowError +
                  `${fieldErrorDetails[row].field} must be ${fieldErrorDetails[row].maxLength} characters or less. `;
              break;
            case 'text':
              if (rowData[row] && !String(rowData[row]).match(/^[a-zA-Z ]*$/))
                rowError =
                  rowError +
                  `No numerical or symbolic values are accepted in ${fieldErrorDetails[row].field}. `;
              break;
            case 'email':
              if (
                rowData[row] &&
                !String(rowData[row]).match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
              )
                rowError =
                  rowError +
                  `Please enter valid ${fieldErrorDetails[row].field}. `;
              break;
            case 'dropDown':
              if (rowData[row]) {
                if (
                  !rowData[fieldErrorDetails[row].dropDownIndex] ||
                  rowData[fieldErrorDetails[row].dropDownIndex]?.includes(
                    '#ERROR',
                  )
                ) {
                  rowError =
                    rowError +
                    `Please select ${fieldErrorDetails[row].field} from the dropdown. `;
                }
              }
              break;
            default:
          }
        });
      }
    }
    return rowError;
  };
  const validateBulkData = (excelData) => {
    let errorsDetails = [];
    let correctData = [];
    let maxRows = 5000;

    if (excelData.length > maxRows) {
      setFileErrorMessage(
        `File contains more than ${maxRows.toLocaleString()} rows. Please split it into smaller files and try again.`,
      );
      return [];
    }

    // Use sets or maps to track duplicates
    const emailEntitlementSet = new Set();

    for (let i = 0; i < excelData.length; i++) {
      const row = excelData[i];
      const email = row[2]?.trim().toLowerCase();
      const entitlement = row[6]?.trim().toLowerCase();
      // The entitlement dropdown (col 78) embeds the specific contract number for each
      // package/product — e.g. Bloomberg has contractNumber=C001 (CSM+), Market Data has
      // contractNumber=C002 (Market Basics). Use that as primary so the same user can get
      // multiple records with different contract numbers. Fall back to the location col (75)
      // contract when the entitlement column has no contract number.
      const entitlementContractNumber =
        String(row[78] || '')
          .split(':')[1]
          ?.trim() || '';
      const locationContractNumber =
        String(row[75] || '')
          .split(':')[1]
          ?.trim() || '';
      const resolvedContractNumber =
        entitlementContractNumber || locationContractNumber || '';
      const officeLocationId =
        String(row[75] || '')
          .split(':')[0]
          ?.trim() || '';
      const contractType = getContractTypeByContractNumber(
        contracts,
        resolvedContractNumber,
      );
      // Include contract number in the key so the same entitlement on two different contracts
      // (same physical location, different package types) is not treated as a duplicate
      const emailEntitlementKey = `${email}|${entitlement}|${resolvedContractNumber}`;
      const excelUserId = row[7] ? String(row[7]).trim() || null : null;
      const contact_Xref_Number = row[8] ? String(row[8]).trim() || null : null;

      let duplicateEntitlement = emailEntitlementSet.has(emailEntitlementKey);

      // Call getRowError with duplicate info
      let error = getRowError(row);

      // Add our own duplicate validation messages
      if (duplicateEntitlement) {
        error += `Duplicate entitlement found. `;
      }

      const locationContractSet =
        locationContractsMapRef.current.get(officeLocationId);
      const entitlementContracts = expandContractNumber(resolvedContractNumber);
      const contractMatchesLocation =
        !!locationContractSet &&
        entitlementContracts.some((c) => locationContractSet.has(c));
      if (
        resolvedContractNumber &&
        officeLocationId &&
        !contractMatchesLocation
      ) {
        error += `Selected entitlement's contract is not associated with the office location. `;
      }

      if (error) {
        let errorModel = {
          firstName: row[0],
          lastName: row[1],
          email: row[2],
          officeLocation: row[3],
          jobFunction: row[4],
          designation: row[5],
          entitlement: row[6],
          userId: excelUserId,
          contact_Xref_Number: contact_Xref_Number,
          error: error,
        };
        errorsDetails.push(errorModel);
      } else {
        // Track unique values only after row passes validation
        emailEntitlementSet.add(emailEntitlementKey);

        const splittedEntitlementCol = String(row[78] || '').split(':');
        const splittedlocationCol = String(row[75] || '').split(':');

        let user = {
          contactId: '-1',
          firstName: row[0],
          lastName: row[1],
          email: row[2],
          officeLocation: row[3],
          officeLocationId: splittedlocationCol[0],
          contractNumber: resolvedContractNumber,
          contractType: contractType,
          jobFunction: row[4],
          designation: row[5],
          isDeleted: 0,
          userid: UserId,
          entitlements: [
            {
              userEntitlementId: '-1',
              entitlement: row[6],
              productComponentId: splittedEntitlementCol[0],
              contractNumber: resolvedContractNumber,
              contractType: contractType,
              distributorId: splittedEntitlementCol[2],
              distributor: splittedEntitlementCol[3],
              distributorPlatform: splittedEntitlementCol[4],
              userId: excelUserId,
              contact_Xref_Number: contact_Xref_Number,
              // FEATURE DISABLED (Release july-2026) - status: isBPMReferral ? Draft : PendingActive,
              status: PendingActive,
            },
          ],
        };
        correctData.push(user);
      }
    }

    setErrorData(errorsDetails);
    return [correctData, errorsDetails];
  };

  const getSuccessOrErrorMessage = (uploadedUsers) => {
    let successMessage = '';
    successMessage =
      uploadedUsers?.length > 0
        ? uploadedUsers.length +
          (uploadedUsers.length === 1
            ? Verbiage.singleUserEntitlement
            : Verbiage.multipleUserEntitlement) +
          ' uploaded successfully'
        : '';
    return successMessage;
  };

  const uploadUserEntitlements = (usersData, errorsData) => {
    let successMessage = '';
    setLoading(true);
    smartHttp
      .post(Endpoints.bulkUserEntitlementApi, usersData)
      .then((resp) => {
        let respUsersEntitlements = resp?.data?.results
          ?.map((item1) => {
            let entitlementResults = item1.entitlements.map((item2) => {
              let finalItem = {
                ...item1,
                entitlement: item2.entitlement,
                userId: item2.userId,
                contact_Xref_Number: item2.contact_Xref_Number,
                productComponentId: item2.productComponentId,
              };
              delete finalItem.entitlements;
              return finalItem;
            });
            return entitlementResults;
          })
          .flat();
        let errorUserEntitlements = respUsersEntitlements.filter(
          (ue) =>
            ue.contactId === '-1' ||
            ue.contactId === '-2' ||
            ue.contactId === '-3' ||
            ue.contactId === '-4',
        );
        let uploadedUsersEntitlements = respUsersEntitlements.filter(
          (ue) =>
            ue.contactId !== '-1' &&
            ue.contactId !== '-2' &&
            ue.contactId !== '-3' &&
            ue.contactId !== '-4',
        );

        if (errorUserEntitlements?.length > 0 || errorsData?.length > 0) {
          errorUserEntitlements.forEach((userEn) => {
            let errorModel = {
              ...userEn,
              error:
                userEn.contactId === '-2'
                  ? 'User already exists.'
                  : userEn.contactId === '-3'
                    ? 'Invalid email domain.'
                    : userEn.contactId === '-4'
                      ? `User already exists at a different Office Location: ${userEn.existingOfficeLocation}`
                      : 'Error in uploading the user.',
            };
            errorsData.push(errorModel);
          });
          setErrorData(errorsData);
        }

        if (respUsersEntitlements?.length > 0) {
          if (!successMessage) {
            successMessage = getSuccessOrErrorMessage(
              uploadedUsersEntitlements,
            );
          }
          setUploadedUsers(uploadedUsersEntitlements);
          getUpdatedUserEntitlements();
        } else successMessage = Verbiage.noValidDataForUserEntitlementUpload;
        setSuccessMessage(successMessage);
        if (errorsData?.length > 0) {
          setErrorMessage(
            errorsData.length +
              (errorsData.length === 1
                ? Verbiage.singleUserEntitlement
                : Verbiage.multipleUserEntitlement) +
              ' cannot be uploaded due to the following errors. Please correct them and upload the file again.',
          );
          setIsErrorGridShow(true);
        }
      })
      .catch((err) => {
        setFileErrorMessage(GenericError.somethingWentWrong);
      })
      .finally(() => setLoading(false));
  };

  const validateHeader = (firstRow) => {
    return (
      firstRow.filter((c) => c != null).join(',') ===
      'First Name,Last Name,Email Address,Office Location Name,Job Function,Role,Entitlement,User ID,Trading Entity Name'
    );
  };
  const validateFile = () => {
    if (files && files[0]) {
      return true;
    } else {
      setFileErrorMessage('Please upload a file');
      return false;
    }
  };
  const validateFileData = (data) => {
    if (data && data.length > 0 && validateHeader(data[0])) {
      if (data.length > 1) {
        return true;
      } else {
        setFileErrorMessage('No data to upload');
        return false;
      }
    } else {
      setFileErrorMessage('Please upload a valid file');
      return false;
    }
  };
  const uploadBulkUsers = () => {
    if (contracts?.length > 0) {
      if (validateFile()) {
        try {
          readXlsxFile(files[0])
            .then(async (rows) => {
              if (validateFileData(rows)) {
                setIsUploadBoxShow(false);
                setFileErrorMessage('');
                let excelRows = rows.filter(
                  (r, i) =>
                    i !== 0 && // Skip header row
                    (r[0] || // first name
                      r[1] || // last name
                      r[2] || // email address
                      r[3] || // office location name
                      r[4] || // job function
                      r[5] || // role
                      r[6] || // entitlement
                      r[7] || // user ID
                      r[8]), // trading entity name
                  // r[75] || // location key
                  // r[76] || // job function key
                  // r[77] || // role key
                  // r[78], // entitlement key
                );
                if (excelRows && excelRows.length > 0) {
                  const [correctData, errorDetails] =
                    await validateBulkData(excelRows);
                  if (correctData?.length > 0)
                    uploadUserEntitlements(correctData, errorDetails);
                  else if (errorDetails?.length > 0) {
                    setErrorMessage(
                      errorDetails.length +
                        (errorDetails.length === 1
                          ? Verbiage.singleUserEntitlement
                          : Verbiage.multipleUserEntitlement) +
                        ' cannot be uploaded due to the following errors. Please correct them and upload the file again.',
                    );
                    setIsErrorGridShow(true);
                  }
                } else {
                  setFileErrorMessage(
                    Verbiage.noValidDataForUserEntitlementUpload,
                  );
                }
              }
            })
            .catch((error) => {
              setFileErrorMessage('No valid data to upload');
            });
        } catch (err) {
          setFileErrorMessage('Please upload a valid file');
        }
      }
    }
  };

  const onSourceUserGridReadyHandler = (params) => {
    gridRef.current = params;
    params.api.sizeColumnsToFit(
      'email,firstName,lastName,officeLocation,jobFunction,designation,entitlement,userId,contact_Xref_Number,error',
      false,
    );
  };

  const onUploadModalClose = () => {
    handleCloseBulkUerEntitlements(false);
  };

  const fetchDataAndDownloadExcel = async () => {
    let filteredContract = contracts?.filter(
      (contract) => contract.checked === true && contract.bulkEntitlementEnable,
    );
    if (filteredContract?.length > 0) {
      setLoading(true);
      const contractNumbers = [
        ...new Set(
          filteredContract.flatMap(
            ({ isVirtualContract, mappedContractNumber, id }) =>
              isVirtualContract && mappedContractNumber
                ? mappedContractNumber
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [id],
          ),
        ),
      ];
      let headersParams = {
        pageSize: maxPageSize,
        filter: `contractNumber IN (${contractNumbers
          ?.map((id) => (id ? '"' + id + '"' : ''))
          .join(',')})`,
        templateType: BulkUploadUserEntitlementsTemplateType,
      };

      if (isVCN) {
        // if VCN then only add vcn contract number
        let csaActionData =
          persistDataStore.getState()?.userActionData?.csaUserActionInfo;
        let mappedCN =
          vcnContract.mappedContractNumber ??
          csaActionData?.data?.mappedContractNumber;
        const contractNumbers = mappedCN
          ?.split(',')
          ?.map((c) => `"${c.trim()}"`)
          ?.join(',');
        headersParams.filter = `contractNumber in(${contractNumbers})`;
      }

      // Apply company-distributor mapping so template dropdown matches configuration
      const hcAccountId = filteredContract?.find(
        (c) => c.checked,
      )?.hC_AccountID;
      if (hcAccountId) {
        try {
          const mappingRes = await smartHttp.getRapid(
            Endpoints.companyDistributorMappingApi,
            {
              pageSize: 5000,
              filter: `hcaccountid:"${hcAccountId}" AND isdeleted:0`,
            },
          );
          const mappedIds = (mappingRes?.data?.results || [])
            .map((m) => m.distributorId ?? m.distributorid)
            .filter((id) => id !== undefined && id !== null);
          if (mappedIds.length > 0) {
            headersParams.distributorIds = mappedIds.join(',');
          }
        } catch {
          /* ignore mapping errors, show all distributors */
        }
      }

      await smartHttp
        .post(Endpoints.bulkUploadTemplateApi, {}, headersParams, {
          axiosConfig: { responseType: 'blob' },
        })
        .then(({ data }) => {
          FileSaver.saveAs(data, 'SFS_Bulk_Upload_User_Entitlements.xlsx');
        })
        .catch((err) => {})
        .finally(() => {
          setLoading(false);
        });
    } else {
      Notify({
        alert: true,
        type: 'warning',
        confirmBtnText: 'OK',
        title: Verbiage.contractNotSelected,
      });
    }
  };
  // Bulk upload drag and drop code
  const [files, setFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setFiles([...acceptedFiles]);
    },
    [files],
  );

  const removeFile = () => {
    setFiles([]);
    setFileErrorMessage('');
    setSuccessMessage('');
    setErrorMessage('');
    setIsUploadBoxShow(true);
    setIsErrorGridShow(false);
  };
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls', '.slk', '.xla', '.xlt', '.xlw'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
    maxFiles: 1,
  });

  const acceptedFileItems = acceptedFiles.map((file) => (
    <div key={file.name} className="file-name">
      <span className="truncate">{file.name}</span>{' '}
      <span className="fw-bolder align-top">
        ({fileSizeConverter(file.size)})
      </span>
      <span>
        <CrossIcon
          onClick={removeFile}
          className="cross-icon"
          alt="Close"
          title="Close"
        ></CrossIcon>
      </span>
    </div>
  ));
  // Bulk upload drag and drop code ends here

  return (
    <>
      <ModalBox
        backdrop="static"
        size="lg"
        show={showBulkUserEntitlements}
        handleClose={onUploadModalClose}
        modalTitle="Bulk Upload User Entitlements"
        showLoader={loading}
      >
        <Row>
          <Col className="d-flex align-items-center justify-content-end">
            <div onClick={fetchDataAndDownloadExcel}>
              <BulkUploadIcon
                className="me-1 cursor-pointer bulk-upload-download-icon"
                alt="Bulk Upload Users"
                title="Bulk Upload Users"
              ></BulkUploadIcon>
              <span className="bulk-upload-text">
                <span>BULK UPLOAD TEMPLATE</span>
              </span>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            {isUploadBoxShow && (
              <div
                className={`${
                  isDragAccept
                    ? 'upload-box-border-blue'
                    : isDragReject
                      ? 'upload-box-border-red'
                      : ''
                } text-center upload-box`}
                {...getRootProps({
                  onClick: removeFile,
                })}
              >
                <span>
                  <input {...getInputProps()} />
                  <CloudBigIcon
                    className="mb-2"
                    alt="Bulk Upload Users Entitlements"
                    title="Bulk Upload Users Entitlements"
                  ></CloudBigIcon>
                  <br />
                  Please download the bulk upload template from the link above
                  and upload here.
                  <br />
                  <span className="non-italic"> Upload XLSX</span>
                </span>
              </div>
            )}
            {files.length > 0 && (
              <div className={`grid-wrapper ${isUploadBoxShow ? 'mt-3' : ''}`}>
                {files.length > 0 ? (
                  <>
                    <div>{acceptedFileItems}</div>
                  </>
                ) : (
                  ''
                )}
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col className={isUploadBoxShow ? 'col-8' : 'col-11'}>
            <div className="error-message-text">{fileErrorMessage}</div>
            <div className="success-message-text">{successMessage}</div>
            <div className="error-message-text">{errorMessage}</div>
          </Col>
          <Col
            className={
              isUploadBoxShow ? 'col-4' : 'col-1 error-grid-export-btn'
            }
          >
            {isUploadBoxShow && (
              <button
                className="btn btn-dark upload-btn btn-opacity float-end"
                disabled={!files.length > 0}
                onClick={uploadBulkUsers}
              >
                UPLOAD
              </button>
            )}
            {isErrorGridShow && (
              <span className="user-grid-icons icon-divider">
                <ExportIcon
                  className="icon-active"
                  alt="Export Active Users Report"
                  width="18"
                  title="Export Active Users Report"
                  onClick={() =>
                    gridRef.current.api.exportDataAsCsv({
                      fileName: 'Userentitlement_Errors_Report.csv',
                    })
                  }
                />
              </span>
            )}
          </Col>
        </Row>
        {isErrorGridShow && (
          <div>
            <Row>
              <Col className="errorgrid">
                <AgGridComponent
                  ref={gridRef}
                  config={defaultConfig}
                  defaultColumnDef={defaultColdef}
                  data={errorData}
                  columns={sourceUsersColumns}
                  gridStyle={sourceUsersGridStyle}
                  onGridReady={onSourceUserGridReadyHandler}
                />
              </Col>
            </Row>
          </div>
        )}
      </ModalBox>
    </>
  );
};

export default memo(BulkUploadUserEntitlements);
